import {v4 as uuidv4} from 'uuid';
import {assertEvent, assign, setup} from 'xstate';

import {
  applyAddLayer,
  applyInitLayers,
  applyRemoveLayer,
  applySetActiveLayer,
  applyUpdateLayerName,
  buildInitialLayerContext,
  LayerBase,
  LayerContextBase,
  LayerMachineInputBase,
} from 'samples/plate-maps/layer-machine/sharedLayerActions';
import {PLATE_MAP_MAX_CONTENTS_LAYERS, ContentsLayerInfo} from 'samples/plate-maps/PlateMapConstants';

const MACHINE_NAME = 'ContentsLayerMachine';

export interface ContentsLayerData extends ContentsLayerInfo, LayerBase {}

export type ContentsLayerEvents =
  | {type: 'initLayers'; layers: Array<ContentsLayerData>}
  | {type: 'addLayer'; layerId: string; name: string}
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string};

export type ContentsLayerContext = LayerContextBase<ContentsLayerData>;

export type ContentsLayerMachineInput = LayerMachineInputBase<ContentsLayerData>;

export function createContentsLayerId(): string {
  return `contents-layer-${uuidv4()}`;
}

export const ContentsLayerMachine = setup({
  types: {
    input: {} as ContentsLayerMachineInput,
    context: {} as ContentsLayerContext,
    events: {} as ContentsLayerEvents,
  },
  actions: {
    initLayers: assign(({context, event}) => {
      assertEvent(event, 'initLayers');
      return applyInitLayers(context, event.layers, PLATE_MAP_MAX_CONTENTS_LAYERS, MACHINE_NAME);
    }),

    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      const newLayer: ContentsLayerData = {layerId: event.layerId, name: event.name};
      return applyAddLayer(context, newLayer, PLATE_MAP_MAX_CONTENTS_LAYERS, MACHINE_NAME);
    }),

    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');
      return applyRemoveLayer(context, event.layerId);
    }),

    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');
      return applySetActiveLayer(context, event.layerId);
    }),

    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');
      return applyUpdateLayerName(context, event.layerId, event.name);
    }),
  },
}).createMachine({
  context: ({input}) => buildInitialLayerContext(input, PLATE_MAP_MAX_CONTENTS_LAYERS, MACHINE_NAME),

  id: 'ContentsLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        initLayers: {actions: 'initLayers'},
        addLayer: {actions: 'addLayer'},
        removeLayer: {actions: 'removeLayer'},
        setActiveLayer: {actions: 'setActiveLayer'},
        updateLayerName: {actions: 'updateLayerName'},
      },
    },
  },
});
